import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { BUBBLE_SHAPES } from "../constants/bubbleShapes";
import { COMIC_FONTS, FONT_SIZE_MIN, FONT_SIZE_MAX, FONT_SIZE_DEFAULT } from "../constants/comicFonts";
import { TAIL_STYLES, normalizeTails } from "../utils/tailGeometry";
import { PANEL_SHAPES } from "../constants/panelShapes";
import { SCREENTONE_PATTERNS } from "../constants/screentones";
import {
  useEditorStore,
  useSelection, useSelectedCell, useSelectedBubble, useSelectedSFX,
  useDarkMode, useMultiSelect, useCanUnmerge,
} from "../store/editorStoreSelectors";
import { makeToolbarStyles } from "./toolbar/toolbarStyles";

const sectionStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

// Inject CSS for hover-reveal help links on dropdown triggers
const helpLinkCSS = `
  .dropdown-help-wrap { position: relative; }
  .dropdown-help-wrap .dropdown-help-link {
    position: absolute; right: -18px; top: 50%; transform: translateY(-50%);
    width: 16px; height: 16px; display: flex; align-items: center; justify-content: center;
    opacity: 0; transition: opacity 0.15s; text-decoration: none; color: #999; z-index: 2;
  }
  .dropdown-help-wrap:hover .dropdown-help-link { opacity: 0.55; }
  .dropdown-help-wrap .dropdown-help-link:hover { opacity: 1; }
`;
if (typeof document !== "undefined" && !document.querySelector("[data-dropdown-help-css]")) {
  const style = document.createElement("style");
  style.setAttribute("data-dropdown-help-css", "");
  style.textContent = helpLinkCSS;
  document.head.appendChild(style);
}

const DropdownWithHelp = ({ anchor, children }) => (
  <div className="dropdown-help-wrap" style={{ position: "relative" }}>
    {children}
    {anchor && (
      <a
        className="dropdown-help-link"
        href={`/tutorial#${anchor}`}
        target="_blank"
        rel="noopener noreferrer"
        title="Tutorial help"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="material-icons" style={{ fontSize: 14 }}>help_outline</span>
      </a>
    )}
  </div>
);

// Space between the palette and what it's attached to, and from desk edges
const GAP = 12;
const EDGE = 8;

/**
 * The selection palette: an index card that floats beside whatever is
 * selected (panel, bubble, SFX, or several panels) holding its controls.
 * It sits above the selection when there's room, otherwise below, points at
 * it with a small notch, follows zoom and scroll, and steps out of the way
 * while you drag.
 *
 * Rendered inside the canvas viewport's overlay layer; finds the selected
 * element on the page by its data attributes.
 */
const SelectionPalette = ({
  onUploadBackground,
  onClearTopItem,
  onAddSpeechBubble,
  onUpdateItem,
  onAddSFX,
  onUpdateSFX,
  onRemoveSFX,
  onUpdateSpeechBubble,
  onRemoveSpeechBubble,
  onDuplicateBubble,
  onDuplicateSFX,
  onUpdateCell,
  onDismissCell,
  onMergePanels,
  onUnmergePanels,
  onReorderItem,
}) => {
  const selection = useSelection();
  const selectedCell = useSelectedCell();
  const selectedBubble = useSelectedBubble();
  const selectedSFX = useSelectedSFX();
  const darkMode = useDarkMode();
  const multiSelect = useMultiSelect();
  const canUnmerge = useCanUnmerge();
  const cells = useEditorStore((s) => s.cells);
  const zoom = useEditorStore((s) => s.zoom);
  const dragging = useEditorStore((s) => Boolean(s.gesture));
  // Board frames are locked to the board's aspect, so they never merge
  const isBoard = useEditorStore((s) => Boolean(s.storyboard));

  const onClearSelection = useEditorStore((s) => s.clearSelection);
  const setMultiSelect = useEditorStore((s) => s.setMultiSelect);
  const setShowAIModal = useEditorStore((s) => s.setShowAIModal);
  const fileInputRef = useRef(null);
  const paletteRef = useRef(null);

  // Dropdown panel state — one panel open at a time
  const [openPanel, setOpenPanel] = useState(null);
  const togglePanel = useCallback((name) => setOpenPanel((prev) => (prev === name ? null : name)), []);

  const isMulti = Array.isArray(multiSelect) && multiSelect.length >= 2;
  const isCellSelected = selection?.type === "cell" && selectedCell;
  const isBubbleSelected = selection?.type === "bubble" && selectedBubble && selectedCell;
  const isSFXSelected = selection?.type === "sfx" && selectedSFX;
  const kindLabel = isMulti
    ? `${multiSelect.length} panels`
    : isBubbleSelected ? "Bubble" : isSFXSelected ? "SFX" : isCellSelected ? "Panel" : null;
  const placeLabel = isMulti ? "" : selectedCell?.key || "";

  // Close any open dropdown when the selection changes
  useEffect(() => {
    setOpenPanel(null);
  }, [selection?.type, selection?.cellId, selection?.bubbleId, selection?.sfxId]);

  // Close the open dropdown on a click outside the palette
  useEffect(() => {
    if (!openPanel) return undefined;
    const handleClick = (e) => {
      if (paletteRef.current && !paletteRef.current.contains(e.target)) setOpenPanel(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openPanel]);

  const triggerUpload = () => {
    if (isCellSelected && fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file && selectedCell) onUploadBackground(selectedCell.id, file);
    if (event.target) event.target.value = "";
  };

  // Clicking palette buttons shouldn't steal focus from the page
  const handleMenuMouseDown = (event) => {
    const isButton = event.target.closest("button");
    if (isButton && !isButton.closest('[data-allow-focus-transfer="true"]')) event.preventDefault();
  };

  // ─── Placement ──────────────────────────────────────────────────────────
  const [place, setPlace] = useState(null); // { left, top, below, notchX }
  const [glide, setGlide] = useState(false);
  const targetKey = isMulti ? `multi:${multiSelect.join(",")}`
    : selection ? `${selection.type}:${selection.cellId}:${selection.bubbleId || selection.sfxId || ""}` : null;

  const targetRect = useCallback(() => {
    const page = document;
    const rectOf = (el) => (el ? el.getBoundingClientRect() : null);
    if (isMulti) {
      const rects = multiSelect
        .map((id) => rectOf(page.querySelector(`[data-cell-id="${CSS.escape(id)}"]`)))
        .filter(Boolean);
      if (!rects.length) return null;
      const left = Math.min(...rects.map((r) => r.left));
      const top = Math.min(...rects.map((r) => r.top));
      const right = Math.max(...rects.map((r) => r.right));
      const bottom = Math.max(...rects.map((r) => r.bottom));
      return { left, top, right, bottom, width: right - left, height: bottom - top };
    }
    if (!selection) return null;
    const itemId = selection.bubbleId || selection.sfxId;
    if (itemId) return rectOf(page.querySelector(`[data-item-id="${CSS.escape(itemId)}"]`));
    return rectOf(page.querySelector(`[data-cell-id="${CSS.escape(selection.cellId)}"]`));
  }, [isMulti, multiSelect, selection]);

  const reposition = useCallback(() => {
    const palette = paletteRef.current;
    const host = palette?.offsetParent;
    const target = targetRect();
    // Nothing to anchor to (not laid out yet, or the selection isn't on
    // screen): pin to the desk's corner rather than vanish
    if (!palette || !host || !target || (target.width === 0 && target.height === 0)) {
      setPlace((prev) => (prev?.pinned ? prev : { left: EDGE, top: EDGE, below: false, notchX: null, pinned: true }));
      return;
    }
    const hostRect = host.getBoundingClientRect();
    const w = palette.offsetWidth;
    const h = palette.offsetHeight;
    // Prefer above; fall back to below; finally pin to the top of the desk
    let top = target.top - hostRect.top - h - GAP;
    let below = false;
    if (top < EDGE) {
      const belowTop = target.bottom - hostRect.top + GAP;
      if (belowTop + h <= hostRect.height - EDGE) {
        top = belowTop;
        below = true;
      } else {
        top = EDGE;
      }
    }
    const centre = target.left + target.width / 2 - hostRect.left;
    // Whole pixels, so sub-pixel jitter can never keep re-placing the card
    top = Math.round(top);
    const left = Math.round(Math.max(EDGE, Math.min(hostRect.width - w - EDGE, centre - w / 2)));
    const notchX = Math.round(Math.max(14, Math.min(w - 14, centre - left)));
    const near = (a, b) => Math.abs(a - b) <= 1;
    setPlace((prev) => (prev && !prev.pinned && near(prev.left, left) && near(prev.top, top)
      && prev.below === below && near(prev.notchX, notchX)
      ? prev
      : { left, top, below, notchX, pinned: false }));
  }, [targetRect]);

  // Re-place after every render that could move things (selection, edits,
  // zoom). If placing keeps triggering renders without a frame in between,
  // stop after a few rounds and let the next frame finish — layout feedback
  // (a re-wrap, a scrollbar) can never become a render loop.
  const syncRounds = useRef(0);
  useLayoutEffect(() => {
    if (syncRounds.current >= 8) {
      const id = requestAnimationFrame(() => {
        syncRounds.current = 0;
        reposition();
      });
      return () => cancelAnimationFrame(id);
    }
    syncRounds.current += 1;
    reposition();
    const id = requestAnimationFrame(() => { syncRounds.current = 0; });
    return () => cancelAnimationFrame(id);
  });

  // Glide to a new selection, but track scrolling/zooming instantly
  useEffect(() => {
    if (!targetKey) return undefined;
    setGlide(true);
    const t = window.setTimeout(() => setGlide(false), 220);
    return () => window.clearTimeout(t);
  }, [targetKey]);

  useEffect(() => {
    const desk = document.querySelector('[data-testid="canvas-desk"]');
    let frame = null;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        reposition();
      });
    };
    desk?.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      desk?.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [reposition, zoom, cells]);

  const {
    labelStyle,
    selectStyle,
    buttonStyle,
    iconButtonStyle,
    activeIconButtonStyle,
    destructiveButtonStyle,
    destructiveIconButtonStyle,
    dropdownTriggerStyle,
    sliderStyle,
    colorSwatchStyle,
    panelStyle,
    ink,
    paper,
    edge,
  } = makeToolbarStyles(darkMode);

  // Dropdowns open away from the selection: upward when the card sits below it
  const effectivePanelStyle = place?.below
    ? { ...panelStyle, top: "auto", bottom: "100%" }
    : panelStyle;

  if (!kindLabel) return null;

  return (
    <div
      ref={paletteRef}
      role="toolbar"
      aria-label={`${kindLabel} controls`}
      data-striply-toolbar
      data-selection-palette
      onMouseDownCapture={handleMenuMouseDown}
      style={{
        position: "absolute",
        left: place?.left ?? EDGE,
        top: place?.top ?? EDGE,
        zIndex: 90,
        // Sized by its content, never by where it sits: an absolutely placed
        // box with auto width shrinks as it nears the right edge, which would
        // re-wrap it, move it again, and loop
        width: "max-content",
        maxWidth: "min(640px, calc(100% - 16px))",
        background: paper,
        color: ink,
        border: `2px solid ${edge}`,
        boxShadow: "4px 4px 0 rgba(0, 0, 0, 0.25)",
        fontFamily: "'Georgia', serif",
        fontSize: 12,
        // Paper, not a pop-up: it slides to a new selection and steps aside
        // while you drag
        opacity: dragging ? 0 : 1,
        pointerEvents: dragging ? "none" : "auto",
        transition: glide
          ? "left 180ms ease, top 180ms ease, opacity 120ms ease"
          : "opacity 120ms ease",
      }}
    >
      {/* Notch pointing at the selection */}
      {place && !place.pinned && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: place.notchX - 7,
            [place.below ? "top" : "bottom"]: -9,
            width: 12,
            height: 12,
            background: paper,
            borderRight: `2px solid ${edge}`,
            borderBottom: `2px solid ${edge}`,
            transform: place.below ? "rotate(-135deg)" : "rotate(45deg)",
          }}
        />
      )}

      {/* Index-card header: what's selected, and a way to let go */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "5px 6px 4px 10px",
          borderBottom: "2px solid rgba(0, 164, 228, 0.45)",
        }}
      >
        <span style={{ fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700 }}>
          {kindLabel}
          {placeLabel && (
            <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: 6, letterSpacing: 1 }}>{placeLabel}</span>
          )}
        </span>
        <button
          type="button"
          aria-label="Deselect"
          title="Deselect (Esc)"
          onClick={() => { onClearSelection(); setMultiSelect([]); }}
          data-allow-focus-transfer="true"
          style={{ border: "none", background: "transparent", color: ink, cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "0 4px" }}
        >
          ×
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, padding: "7px 9px" }}>
      {!isBoard && Array.isArray(multiSelect) && multiSelect.length >= 2 && (
        <div style={sectionStyle}>
          <span style={labelStyle}>{multiSelect.length} panels selected</span>
          <button type="button" style={buttonStyle} onClick={onMergePanels} title="Merge selected panels">
            <span className="material-icons" aria-hidden="true" style={{ fontSize: 16, marginRight: 2 }}>call_merge</span>
            Merge
          </button>
        </div>
      )}

      {/* ===== CELL SELECTED ===== */}
      {isCellSelected && !isBubbleSelected && !isSFXSelected && (
        <>
          <div style={sectionStyle}>
            <button
              type="button"
              aria-label="Upload background"
              title="Upload background"
              style={iconButtonStyle}
              onClick={triggerUpload}
              disabled={!isCellSelected}
            >
              <span className="material-icons" aria-hidden="true">
                add_photo_alternate
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <button
              type="button"
              aria-label="Add speech bubble"
              title="Add speech bubble"
              style={iconButtonStyle}
              onClick={() => isCellSelected && onAddSpeechBubble(selectedCell.id)}
              disabled={!isCellSelected}
            >
              <span className="material-icons" aria-hidden="true">
                mode_comment
              </span>
            </button>
            {onAddSFX && (
              <button
                type="button"
                aria-label="Add SFX text"
                title="Add SFX text"
                style={iconButtonStyle}
                onClick={() => isCellSelected && onAddSFX(selectedCell.id)}
                disabled={!isCellSelected}
              >
                <span className="material-icons" aria-hidden="true">
                  text_fields
                </span>
              </button>
            )}
            {setShowAIModal && (
              <button
                type="button"
                aria-label="AI Generate image"
                title="AI Generate"
                style={iconButtonStyle}
                onClick={() => selectedCell && setShowAIModal(true)}
                disabled={!isCellSelected}
              >
                <span className="material-icons" aria-hidden="true">
                  auto_awesome
                </span>
              </button>
            )}
            <button
              type="button"
              aria-label="Clear top item"
              title="Clear top item"
              style={iconButtonStyle}
              onClick={() => isCellSelected && onClearTopItem(selectedCell.id)}
              disabled={!isCellSelected || !(selectedCell.items || []).length}
            >
              <span className="material-icons" aria-hidden="true">
                layers_clear
              </span>
            </button>
            <button
              type="button"
              aria-label="Remove cell"
              title="Remove cell"
              style={destructiveIconButtonStyle}
              onClick={() => isCellSelected && onDismissCell(selectedCell.id)}
              disabled={!isCellSelected}
            >
              <span className="material-icons" aria-hidden="true">
                delete
              </span>
            </button>
          </div>

          {/* Panel dropdown: shape, z-order, border, screentone, unmerge */}
          {onUpdateCell && (
            <DropdownWithHelp anchor="panels">
              <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("cellPanel")}>
                Panel <span style={{ fontSize: 10 }}>&#9662;</span>
              </button>
              {openPanel === "cellPanel" && (
                <div style={effectivePanelStyle}>
                  <span style={labelStyle}>Shape:</span>
                  <select
                    aria-label="Panel shape"
                    value={selectedCell?.panelShape || "rectangle"}
                    onChange={(e) => onUpdateCell({ panelShape: e.target.value })}
                    style={selectStyle}
                  >
                    {PANEL_SHAPES.map((s) => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                  <span style={labelStyle}>Z:</span>
                  <button
                    type="button"
                    title="Bring forward"
                    style={iconButtonStyle}
                    onClick={() => onUpdateCell({ zIndex: (selectedCell?.zIndex || 0) + 1 })}
                  >
                    <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>arrow_upward</span>
                  </button>
                  <button
                    type="button"
                    title="Send backward"
                    style={iconButtonStyle}
                    onClick={() => onUpdateCell({ zIndex: Math.max(0, (selectedCell?.zIndex || 0) - 1) })}
                  >
                    <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>arrow_downward</span>
                  </button>
                  <span style={labelStyle}>Border:</span>
                  <input
                    type="number"
                    aria-label="Border width"
                    title="Border width (px)"
                    min={0}
                    max={5}
                    value={selectedCell?.borderWidth ?? 2}
                    onChange={(e) => onUpdateCell({ borderWidth: Math.max(0, Math.min(5, Number(e.target.value))) })}
                    style={{ width: 36, ...selectStyle }}
                  />
                  <select
                    aria-label="Border style"
                    value={selectedCell?.borderStyle || "solid"}
                    onChange={(e) => onUpdateCell({ borderStyle: e.target.value })}
                    style={selectStyle}
                  >
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                    <option value="double">Double</option>
                  </select>
                  <input
                    type="color"
                    aria-label="Border color"
                    title="Border color"
                    value={selectedCell?.borderColor || "#888888"}
                    onChange={(e) => onUpdateCell({ borderColor: e.target.value })}
                    style={{ ...colorSwatchStyle, width: 28, height: 28 }}
                  />
                  <span style={labelStyle}>Tone:</span>
                  <select
                    aria-label="Screentone pattern"
                    value={selectedCell?.screentone || "none"}
                    onChange={(e) => onUpdateCell({ screentone: e.target.value })}
                    style={selectStyle}
                  >
                    {SCREENTONE_PATTERNS.map((p) => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                  {selectedCell?.screentone && selectedCell.screentone !== "none" && (
                    <input
                      type="range"
                      aria-label="Screentone opacity"
                      min={0.05}
                      max={0.8}
                      step={0.05}
                      value={selectedCell?.screentoneOpacity ?? 0.3}
                      onChange={(e) => onUpdateCell({ screentoneOpacity: Number(e.target.value) })}
                      style={{ width: 50, ...sliderStyle }}
                      title="Screentone opacity"
                    />
                  )}
                  {canUnmerge && (
                    <button type="button" style={buttonStyle} onClick={onUnmergePanels} title="Unmerge panels">
                      <span className="material-icons" aria-hidden="true" style={{ fontSize: 16, marginRight: 2 }}>call_split</span>
                      Unmerge
                    </button>
                  )}
                </div>
              )}
            </DropdownWithHelp>
          )}

          {/* Background dropdown (conditional on bg existing) */}
          {onUpdateItem && selectedCell && (selectedCell.items || []).find((i) => i.type === "background") && (() => {
            const bg = (selectedCell.items || []).find((i) => i.type === "background");
            return (
              <DropdownWithHelp anchor="backgrounds">
                <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("cellBg")}>
                  Background <span style={{ fontSize: 10 }}>&#9662;</span>
                </button>
                {openPanel === "cellBg" && (
                  <div style={effectivePanelStyle}>
                    <span style={labelStyle}>Zoom:</span>
                    <input
                      type="range"
                      aria-label="Background zoom"
                      min={50}
                      max={300}
                      step={5}
                      value={bg.zoom || 100}
                      onChange={(e) => onUpdateItem(bg.id, { zoom: Number(e.target.value) })}
                      style={{ width: 80, ...sliderStyle }}
                    />
                    <span style={{ fontSize: 11, minWidth: 28 }}>{bg.zoom || 100}%</span>
                    <button
                      type="button"
                      title="Reset zoom/pan"
                      style={iconButtonStyle}
                      onClick={() => onUpdateItem(bg.id, { zoom: 100, panX: 0, panY: 0 })}
                    >
                      <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>center_focus_strong</span>
                    </button>
                  </div>
                )}
              </DropdownWithHelp>
            );
          })()}
        </>
      )}

      {/* ===== BUBBLE SELECTED ===== */}
      {isBubbleSelected && (
        <>
          <div style={sectionStyle}>
            <select
              aria-label="Bubble shape"
              value={selectedBubble?.shape || "rounded"}
              onChange={(e) => onUpdateSpeechBubble({ shape: e.target.value })}
              style={selectStyle}
            >
              {BUBBLE_SHAPES.map((shape) => (
                <option key={shape.key} value={shape.key}>
                  {shape.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              title={selectedBubble?.locked ? "Unlock bubble" : "Lock bubble"}
              style={selectedBubble?.locked ? activeIconButtonStyle : iconButtonStyle}
              onClick={() => onUpdateSpeechBubble({ locked: !selectedBubble?.locked })}
            >
              <span className="material-icons" aria-hidden="true" style={{ fontSize: 18 }}>
                {selectedBubble?.locked ? "lock" : "lock_open"}
              </span>
            </button>
            <button
              type="button"
              title="Duplicate bubble (Ctrl+D)"
              style={iconButtonStyle}
              onClick={onDuplicateBubble}
              disabled={!isBubbleSelected}
            >
              <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>content_copy</span>
            </button>
            <button
              type="button"
              style={destructiveButtonStyle}
              onClick={onRemoveSpeechBubble}
              disabled={!isBubbleSelected}
            >
              Remove
            </button>
          </div>

          {/* Arrange: layer order, tucked away to keep the card to one row */}
          <DropdownWithHelp anchor="layers">
            <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("arrange")}>
              Arrange <span style={{ fontSize: 10 }}>&#9662;</span>
            </button>
            {openPanel === "arrange" && (
              <div style={{ ...effectivePanelStyle, minWidth: 0, flexDirection: "column", alignItems: "stretch", gap: 4, padding: 8 }}>
                {[
                  ["toFront", "vertical_align_top", "Bring to front", "⌘⇧]"],
                  ["forward", "arrow_upward", "Bring forward", "⌘]"],
                  ["backward", "arrow_downward", "Send backward", "⌘["],
                  ["toBack", "vertical_align_bottom", "Send to back", "⌘⇧["],
                ].map(([direction, icon, label, keys]) => (
                  <button
                    key={direction}
                    type="button"
                    onClick={() => onReorderItem(direction)}
                    style={{ ...buttonStyle, display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", textAlign: "left" }}
                  >
                    <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>{icon}</span>
                    <span style={{ flex: 1 }}>{label}</span>
                    <span style={{ opacity: 0.55, fontSize: 11 }}>{keys}</span>
                  </button>
                ))}
              </div>
            )}
          </DropdownWithHelp>

          {/* Tail dropdown */}
          <DropdownWithHelp anchor="tails">
            <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("bubbleTail")}>
              Tail <span style={{ fontSize: 10 }}>&#9662;</span>
            </button>
            {openPanel === "bubbleTail" && (
              <div style={effectivePanelStyle}>
                <select
                  aria-label="Tail visibility"
                  value={selectedBubble?.tailEnabled ? "on" : "off"}
                  onChange={(e) => onUpdateSpeechBubble({ tailEnabled: e.target.value === "on" })}
                  style={selectStyle}
                >
                  <option value="on">Tail on</option>
                  <option value="off">Tail off</option>
                </select>
                <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                  Width:
                  <input
                    type="range"
                    aria-label="Tail width"
                    min={8}
                    max={60}
                    step={2}
                    value={(() => {
                      const tails = normalizeTails(selectedBubble);
                      return tails[0]?.width || selectedBubble?.tailWidth || 20;
                    })()}
                    onChange={(e) => {
                      const width = Number(e.target.value);
                      const currentTails = normalizeTails(selectedBubble);
                      const updatedTails = currentTails.map(t => ({ ...t, width }));
                      onUpdateSpeechBubble({ tailWidth: width, tails: updatedTails });
                    }}
                    disabled={!selectedBubble?.tailEnabled}
                    style={{ width: 80, ...sliderStyle }}
                  />
                  <span style={{ minWidth: 20, textAlign: "right" }}>{(() => {
                    const tails = normalizeTails(selectedBubble);
                    return tails[0]?.width || selectedBubble?.tailWidth || 20;
                  })()}</span>
                </label>
                {selectedBubble?.tailEnabled && (() => {
                  const currentTails = normalizeTails(selectedBubble);
                  return (
                    <>
                      <select
                        aria-label="Tail style"
                        title="Tail style (applies to first tail)"
                        value={currentTails[0]?.style || "pointed"}
                        onChange={(e) => {
                          const newTails = currentTails.map((t, i) =>
                            i === 0 ? { ...t, style: e.target.value } : t
                          );
                          onUpdateSpeechBubble({ tails: newTails });
                        }}
                        style={selectStyle}
                      >
                        {TAIL_STYLES.map((s) => (
                          <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        title="Add tail"
                        style={iconButtonStyle}
                        disabled={currentTails.length >= 3}
                        onClick={() => {
                          const newTail = {
                            id: `tail-${Date.now()}`,
                            tipX: selectedBubble.width * 0.3,
                            tipY: selectedBubble.height + 35,
                            width: 20,
                            style: "pointed",
                          };
                          onUpdateSpeechBubble({ tails: [...currentTails, newTail] });
                        }}
                      >
                        <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>add</span>
                      </button>
                      {currentTails.length > 1 && (
                        <button
                          type="button"
                          title="Remove last tail"
                          style={iconButtonStyle}
                          onClick={() => {
                            onUpdateSpeechBubble({ tails: currentTails.slice(0, -1) });
                          }}
                        >
                          <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>remove</span>
                        </button>
                      )}
                    </>
                  );
                })()}
                <button
                  type="button"
                  aria-label="Reset tail position"
                  title="Reset tail"
                  style={iconButtonStyle}
                  onClick={() => {
                    if (selectedBubble) {
                      onUpdateSpeechBubble({
                        tailTipX: selectedBubble.width / 2,
                        tailTipY: selectedBubble.height + 30,
                        tailWidth: 20,
                      });
                    }
                  }}
                  disabled={!selectedBubble?.tailEnabled}
                >
                  <span className="material-icons" aria-hidden="true">restart_alt</span>
                </button>
              </div>
            )}
          </DropdownWithHelp>

          {/* Text dropdown */}
          <DropdownWithHelp anchor="text-styling">
            <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("bubbleText")}>
              Text <span style={{ fontSize: 10 }}>&#9662;</span>
            </button>
            {openPanel === "bubbleText" && (
              <div style={effectivePanelStyle}>
                <select
                  aria-label="Text alignment"
                  value={selectedBubble?.textAlign || "left"}
                  onChange={(e) => onUpdateSpeechBubble({ textAlign: e.target.value })}
                  style={selectStyle}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
                <select
                  aria-label="Font family"
                  value={selectedBubble?.fontFamily || "inherit"}
                  onChange={(e) => onUpdateSpeechBubble({ fontFamily: e.target.value })}
                  style={selectStyle}
                >
                  {COMIC_FONTS.map((f) => (
                    <option key={f.key} value={f.family}>{f.label}</option>
                  ))}
                </select>
                <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                  Size:
                  <input
                    type="number"
                    aria-label="Font size"
                    min={FONT_SIZE_MIN}
                    max={FONT_SIZE_MAX}
                    value={selectedBubble?.fontSize || FONT_SIZE_DEFAULT}
                    onChange={(e) => onUpdateSpeechBubble({ fontSize: Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, Number(e.target.value))) })}
                    style={{ width: 40, ...selectStyle }}
                  />
                </label>
                <button
                  type="button"
                  title="Bold"
                  style={{ ...(selectedBubble?.fontWeight === "bold" ? activeIconButtonStyle : iconButtonStyle), fontWeight: "bold", fontSize: 14 }}
                  onClick={() => onUpdateSpeechBubble({ fontWeight: selectedBubble?.fontWeight === "bold" ? "normal" : "bold" })}
                >B</button>
                <button
                  type="button"
                  title="Italic"
                  style={{ ...(selectedBubble?.fontStyle === "italic" ? activeIconButtonStyle : iconButtonStyle), fontStyle: "italic", fontSize: 14 }}
                  onClick={() => onUpdateSpeechBubble({ fontStyle: selectedBubble?.fontStyle === "italic" ? "normal" : "italic" })}
                >I</button>
                <button
                  type="button"
                  title="ALL CAPS"
                  style={{ ...(selectedBubble?.textTransform === "uppercase" ? activeIconButtonStyle : iconButtonStyle), fontSize: 11 }}
                  onClick={() => onUpdateSpeechBubble({ textTransform: selectedBubble?.textTransform === "uppercase" ? "none" : "uppercase" })}
                >AA</button>
              </div>
            )}
          </DropdownWithHelp>

          {/* Colors dropdown */}
          <DropdownWithHelp anchor="colors">
            <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("bubbleColors")}>
              Colors <span style={{ fontSize: 10 }}>&#9662;</span>
            </button>
            {openPanel === "bubbleColors" && (
              <div style={effectivePanelStyle}>
                <label title="Background color" style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 11 }}>
                  BG
                  <input
                    type="color"
                    aria-label="Bubble background color"
                    value={selectedBubble?.bgColor || "#ffffff"}
                    onChange={(e) => onUpdateSpeechBubble({ bgColor: e.target.value })}
                    style={colorSwatchStyle}
                  />
                </label>
                <label title="Border color" style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 11 }}>
                  Bdr
                  <input
                    type="color"
                    aria-label="Bubble border color"
                    value={selectedBubble?.borderColor || "#cccccc"}
                    onChange={(e) => onUpdateSpeechBubble({ borderColor: e.target.value })}
                    style={colorSwatchStyle}
                  />
                </label>
                <label title="Text color" style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 11 }}>
                  Txt
                  <input
                    type="color"
                    aria-label="Bubble text color"
                    value={selectedBubble?.textColor || "#1f2937"}
                    onChange={(e) => onUpdateSpeechBubble({ textColor: e.target.value })}
                    style={colorSwatchStyle}
                  />
                </label>
                <label title="Opacity" style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 11 }}>
                  Op
                  <input
                    type="range"
                    aria-label="Bubble opacity"
                    min={0.1}
                    max={1}
                    step={0.05}
                    value={selectedBubble?.opacity ?? 1}
                    onChange={(e) => onUpdateSpeechBubble({ opacity: Number(e.target.value) })}
                    style={{ width: 50, ...sliderStyle }}
                  />
                </label>
              </div>
            )}
          </DropdownWithHelp>
        </>
      )}

      {/* ===== SFX SELECTED ===== */}
      {isSFXSelected && (
        <>
          <div style={sectionStyle}>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              Size:
              <input
                type="number"
                aria-label="SFX font size"
                min={24}
                max={120}
                value={selectedSFX.fontSize || 64}
                onChange={(e) => onUpdateSFX({ fontSize: Math.max(24, Math.min(120, Number(e.target.value))) })}
                style={{ width: 44, ...selectStyle }}
              />
            </label>
            <button
              type="button"
              title={selectedSFX?.locked ? "Unlock SFX" : "Lock SFX"}
              style={selectedSFX?.locked ? activeIconButtonStyle : iconButtonStyle}
              onClick={() => onUpdateSFX({ locked: !selectedSFX?.locked })}
            >
              <span className="material-icons" aria-hidden="true" style={{ fontSize: 18 }}>
                {selectedSFX?.locked ? "lock" : "lock_open"}
              </span>
            </button>
            <button
              type="button"
              title="Duplicate SFX (Ctrl+D)"
              style={iconButtonStyle}
              onClick={onDuplicateSFX}
            >
              <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>content_copy</span>
            </button>
            <button
              type="button"
              style={destructiveButtonStyle}
              onClick={onRemoveSFX}
            >
              Remove
            </button>
          </div>

          {/* Arrange: layer order, tucked away to keep the card to one row */}
          <DropdownWithHelp anchor="layers">
            <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("arrange")}>
              Arrange <span style={{ fontSize: 10 }}>&#9662;</span>
            </button>
            {openPanel === "arrange" && (
              <div style={{ ...effectivePanelStyle, minWidth: 0, flexDirection: "column", alignItems: "stretch", gap: 4, padding: 8 }}>
                {[
                  ["toFront", "vertical_align_top", "Bring to front", "⌘⇧]"],
                  ["forward", "arrow_upward", "Bring forward", "⌘]"],
                  ["backward", "arrow_downward", "Send backward", "⌘["],
                  ["toBack", "vertical_align_bottom", "Send to back", "⌘⇧["],
                ].map(([direction, icon, label, keys]) => (
                  <button
                    key={direction}
                    type="button"
                    onClick={() => onReorderItem(direction)}
                    style={{ ...buttonStyle, display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", textAlign: "left" }}
                  >
                    <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>{icon}</span>
                    <span style={{ flex: 1 }}>{label}</span>
                    <span style={{ opacity: 0.55, fontSize: 11 }}>{keys}</span>
                  </button>
                ))}
              </div>
            )}
          </DropdownWithHelp>

          {/* Style dropdown */}
          <DropdownWithHelp anchor="sfx">
            <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("sfxStyle")}>
              Style <span style={{ fontSize: 10 }}>&#9662;</span>
            </button>
            {openPanel === "sfxStyle" && (
              <div style={effectivePanelStyle}>
                <select
                  aria-label="SFX font"
                  value={selectedSFX.fontFamily || "'Bangers', cursive"}
                  onChange={(e) => onUpdateSFX({ fontFamily: e.target.value })}
                  style={selectStyle}
                >
                  {COMIC_FONTS.map((f) => (
                    <option key={f.key} value={f.family}>{f.label}</option>
                  ))}
                </select>
                <label title="Fill color" style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 11 }}>
                  Fill
                  <input
                    type="color"
                    aria-label="SFX color"
                    value={selectedSFX.color || "#ff0000"}
                    onChange={(e) => onUpdateSFX({ color: e.target.value })}
                    style={colorSwatchStyle}
                  />
                </label>
                <label title="Stroke color" style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 11 }}>
                  Stroke
                  <input
                    type="color"
                    aria-label="SFX stroke color"
                    value={selectedSFX.strokeColor || "#000000"}
                    onChange={(e) => onUpdateSFX({ strokeColor: e.target.value })}
                    style={colorSwatchStyle}
                  />
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                  Rot:
                  <input
                    type="number"
                    aria-label="SFX rotation"
                    min={-180}
                    max={180}
                    value={selectedSFX.rotation || 0}
                    onChange={(e) => onUpdateSFX({ rotation: Number(e.target.value) })}
                    style={{ width: 44, ...selectStyle }}
                  />
                </label>
              </div>
            )}
          </DropdownWithHelp>

          {/* Scale dropdown */}
          <DropdownWithHelp anchor="scaling">
            <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("sfxScale")}>
              Scale <span style={{ fontSize: 10 }}>&#9662;</span>
            </button>
            {openPanel === "sfxScale" && (
              <div style={effectivePanelStyle}>
                <label title="Horizontal scale" style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 11 }}>
                  W
                  <input
                    type="range"
                    aria-label="SFX horizontal scale"
                    min={0.3}
                    max={4}
                    step={0.05}
                    value={selectedSFX.scaleX ?? 1}
                    onChange={(e) => onUpdateSFX({ scaleX: Number(e.target.value) })}
                    style={{ width: 60, ...sliderStyle }}
                  />
                  <span style={{ minWidth: 28, fontSize: 11 }}>{Math.round((selectedSFX.scaleX ?? 1) * 100)}%</span>
                </label>
                <label title="Vertical scale" style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 11 }}>
                  H
                  <input
                    type="range"
                    aria-label="SFX vertical scale"
                    min={0.3}
                    max={4}
                    step={0.05}
                    value={selectedSFX.scaleY ?? 1}
                    onChange={(e) => onUpdateSFX({ scaleY: Number(e.target.value) })}
                    style={{ width: 60, ...sliderStyle }}
                  />
                  <span style={{ minWidth: 28, fontSize: 11 }}>{Math.round((selectedSFX.scaleY ?? 1) * 100)}%</span>
                </label>
                <button
                  type="button"
                  title="Reset scale to 100%"
                  style={iconButtonStyle}
                  onClick={() => onUpdateSFX({ scaleX: 1, scaleY: 1 })}
                >
                  <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>restart_alt</span>
                </button>
              </div>
            )}
          </DropdownWithHelp>
        </>
      )}
      </div>
    </div>
  );
};

export default SelectionPalette;
